import * as THREE from "three";
import Experience from "../../../Experience.js";

export default class OceanFloor {
    constructor(bounds) {
        this.experience = new Experience();
        this.bounds = bounds;
        const noiseT = this.experience.noiseTexture;

        // Read pixels for CPU deformation
        const noiseWidth = noiseT.width;
        const noiseHeight = noiseT.height;
        const noiseData = new Uint8Array(noiseWidth * noiseHeight * 4);
        this.experience.noiseMaker.renderer.readRenderTargetPixels(
            this.experience.noiseTextureRT,
            0,
            0,
            noiseWidth,
            noiseHeight,
            noiseData,
        );

        // keep the geometry resolution high enough to capture the ripples and noise details
        // but not too high to avoid performance issues
        const geometry = new THREE.PlaneGeometry(
            bounds.width * 2,
            bounds.depth * 2,
            400,
            400,
        );

        const positionAttribute = geometry.getAttribute("position");
        for (let i = 0; i < positionAttribute.count; i++) {
            const x = positionAttribute.getX(i);
            const y = positionAttribute.getY(i); // original y in PlaneGeometry

            // UV mapping
            const u = x / (bounds.width * 2) + 0.5;
            const v = y / (bounds.depth * 2) + 0.5;

            // Sample noise
            const px = Math.floor(Math.min(u, 0.999) * noiseWidth);
            const py = Math.floor(Math.min(v, 0.999) * noiseHeight);
            const pixelIndex = (py * noiseWidth + px) * 4 + 1; // getting just the green channel
            const noiseValue = noiseData[pixelIndex] / 255;

            // Noise-warped sine wave for sand ripples
            const rippleFreqX = 0.4;
            const rippleFreqY = 0.1;
            const rippleAmp = 0.3;
            const warpValue = noiseValue * 5;
            const ripple =
                Math.sin(x * rippleFreqX + y * rippleFreqY + warpValue) *
                rippleAmp;

            // General terrain variance
            const elevation = ripple;

            positionAttribute.setZ(i, elevation);
        }

        geometry.computeVertexNormals();

        this.uniforms = {
            uFadeRadius: { value: Math.min(bounds.width, bounds.depth) * 0.8 },
            uFadeSpread: { value: Math.min(bounds.width, bounds.depth) * 0.2 },
        };

        const material = new THREE.MeshPhongMaterial({
            color: new THREE.Color("#a68f6e"),
            shininess: 5,
            // roughness: 0.9,
            // metalness: 0.0,
            // wireframe: true,
        });
        this.params = {
            color: material.color.getHex(THREE.SRGBColorSpace),
        };

        material.onBeforeCompile = (shader) => {
            shader.uniforms.uFadeRadius = this.uniforms.uFadeRadius;
            shader.uniforms.uFadeSpread = this.uniforms.uFadeSpread;

            shader.fragmentShader = shader.fragmentShader.replace(
                "#include <common>",
                `#include <common>
                uniform float uFadeRadius;
                uniform float uFadeSpread;
                `,
            );

            shader.fragmentShader = shader.fragmentShader.replace(
                "#include <fog_fragment>",
                `#include <fog_fragment>
                float causticDist = length(vCausticWorldPosition.xz);
                float alpha = smoothstep(uFadeRadius, uFadeRadius + uFadeSpread, causticDist);
                gl_FragColor.rgb = mix(gl_FragColor.rgb, fogColor, alpha);`,
            );
        };

        this.mesh = new THREE.Mesh(geometry, material);
        this.mesh.rotation.x = -Math.PI / 2;
        this.mesh.position.y = bounds.min.y;
        this.mesh.receiveShadow = true;

        // Debug
        if (this.experience.debug.active) {
            this.debugFolder =
                this.experience.debug.ui.addFolder("Ocean Floor");
            this.debugFolder
                .addColor(this.params, "color")
                .name("Floor Color")
                .onChange((v) => {
                    this.mesh.material.color.setHex(v, THREE.SRGBColorSpace);
                });

            this.debugFolder
                .add(this.uniforms.uFadeRadius, "value")
                .min(0)
                .max(100)
                .step(0.1)
                .name("fadeRadius");

            this.debugFolder
                .add(this.uniforms.uFadeSpread, "value")
                .min(0)
                .max(50)
                .step(0.1)
                .name("fadeSpread");
        }
    }

    update() {}
}
